import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Download, FileText, LockKeyhole, Mail, Newspaper, ShieldCheck, UnlockKeyhole } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const PUBLIC_BRIEF = '/documents/SALAR_AI_Sovereignty_Executive_Brief_WP_2026_006.pdf';
const VIDEO_URL = '/media/vikrant-salar/who-owns-the-intelligence.mp4?v=4';
const VIDEO_POSTER = '/img/news/who-owns-poster.webp?v=4';

export default function AISovereigntyReport() {
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch('/api/ai-sovereignty-auth', { credentials: 'same-origin' })
      .then((response) => response.json())
      .then((data) => setAuthenticated(Boolean(data.authenticated)))
      .catch(() => {});
  }, []);

  async function unlock(event) {
    event.preventDefault();
    setBusy(true); setError('');
    try {
      const response = await fetch('/api/ai-sovereignty-auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ password }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Access could not be verified.');
      setAuthenticated(true); setPassword('');
      window.open('/api/ai-sovereignty-report', '_blank', 'noopener,noreferrer');
    } catch (reason) {
      setError(reason.message);
    } finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen bg-[#07101f] text-white">
      <Helmet>
        <title>AI Sovereignty & Model Centralization | SALAR Technologies</title>
        <meta name="description" content="SALAR Technologies maps who controls AI, the semiconductor stack beneath it, and the strategic meaning of sovereignty as controlled dependency with credible exit options." />
        <link rel="canonical" href="https://www.salartechnologies.com/ai-sovereignty-report" />
        <meta property="og:image" content="https://www.salartechnologies.com/img/news/salar-ai-sovereignty-map-2026.webp" />
      </Helmet>

      <section className="relative overflow-hidden border-b border-cyan-400/20">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "linear-gradient(90deg, rgba(5,13,27,.98) 0%, rgba(5,13,27,.74) 48%, rgba(5,13,27,.35) 100%), url('/img/news/salar-ai-sovereignty-map-2026.webp')" }}
        />
        <div className="container relative z-10 mx-auto max-w-4xl px-5 py-14 sm:px-6 sm:py-20 lg:py-28">
          <div className="max-w-4xl">
            <Badge variant="outline" className="mb-5 border-amber-300/70 text-amber-200">SALAR-WP-2026-006 Ÿ AUGUST 2026</Badge>
            <h1 className="mb-6 text-4xl font-black tracking-tight md:text-6xl">Who Controls AI?</h1>
            <p className="max-w-3xl text-xl font-semibold leading-relaxed text-cyan-100 md:text-2xl">AI sovereignty is decided where models, compute, chips, energy and policy converge.</p>
            <p className="mt-6 max-w-3xl leading-relaxed text-blue-100">A strategic atlas of the United States as the clear leader, China as the challenger, Europe as the regulatory power, India as the wildcard, and Taiwan as the decisive physical convergence point.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild className="bg-cyan-400 font-bold text-[#07101f] hover:bg-cyan-300"><a href={PUBLIC_BRIEF} target="_blank" rel="noopener noreferrer"><Download className="mr-2" size={18} />Download 3-Page Brief</a></Button>
              <Button asChild variant="outline" className="border-white/40 text-white hover:bg-white/10"><a href="mailto:vikrant@salartechnologies.com?subject=Request%20access%20to%20SALAR-WP-2026-006"><Mail className="mr-2" size={18} />Request Access</a></Button>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-cyan-400/20 bg-[#050b14] py-12 sm:py-16">
        <div className="container mx-auto max-w-5xl px-5 sm:px-6">
          <div className="mb-6 text-center">
            <h2 className="text-2xl sm:text-4xl font-black tracking-tight text-white">Who Owns the Intelligence?</h2>
            <p className="mt-2 text-sm sm:text-base text-blue-100/70">3 min</p>
          </div>
          <div style={{ borderRadius: '1rem', overflow: 'hidden', border: '1px solid rgba(34,211,238,0.25)' }}>
            <video
              src={VIDEO_URL}
              controls
              playsInline
              webkit-playsinline="true"
              preload="metadata"
              poster={VIDEO_POSTER}
              style={{
                display: 'block',
                width: '100%',
                height: 'auto',
                minHeight: '1px',
                backgroundColor: '#000'
              }}
            >
              Your browser does not support HTML5 video.
            </video>
          </div>
        </div>
      </section>

      <section className="container mx-auto grid max-w-7xl gap-8 px-6 py-16 lg:grid-cols-[1.15fr_.85fr]">
        <div className="space-y-8">
          <Card className="border-cyan-400/25 bg-[#0F1A2B]/95 text-white"><CardContent className="p-6 sm:p-7 md:p-9">
            <div className="mb-4 flex items-center gap-3 text-cyan-300"><FileText size={22} /><span className="font-bold uppercase tracking-widest">Decision summary</span></div>
            <h2 className="text-2xl font-black leading-tight sm:text-3xl">What this paper is saying</h2>
            <p className="mt-4 text-base leading-7 text-blue-100 sm:text-lg">AI power has two inseparable layers: intelligence above the line and industrial capacity below it. A country leads only when it can design, build, deploy, power and sustain both.</p>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {
                [
                  ['United States', 'Clear leader', 'Frontier models, accelerator design, cloud scale and capital.'],
                  ['China', 'Primary challenger', 'Scale, research depth and efficiencyŸconstrained by advanced-chip access.'],
                  ['Europe', 'Regulatory power', 'Standards and equipment leverage without comparable frontier scale.'],
                  ['India', 'Strategic wildcard', 'Talent and adoption strength; frontier compute remains the constraint.'],
                ].map(([region, role, reason]) => <div key={region} className="rounded-xl border border-cyan-400/20 bg-[#07101f]/80 p-4"><div className="text-xs font-black uppercase tracking-[0.16em] text-cyan-300">{region}</div><div className="mt-1 text-lg font-black text-white">{role}</div><p className="mt-2 text-sm leading-6 text-blue-200">{reason}</p></div>)
              }
            </div>
            <div className="mt-5 rounded-xl border-amber-300/30 bg-amber-300/10 p-4 text-sm leading-6 text-amber-50"><strong className="text-amber-200">Decisive variable:</strong> Taiwan-centered leading-edge fabrication and packaging set the ceiling; HBM, lithography, cloud capacity and power determine how high that ceiling can be used.</div>
          </CardContent></Card>
          <figure className="overflow-hidden rounded-2xl border border-cyan-400/25 bg-[#06101f] shadow-2xl">
            <img src="/img/news/salar-semiconductor-control-map-2026.webp" alt="Semiconductor Control Map 2026 showing the global AI chip supply chain" className="block h-auto w-full" />
            <figcaption className="border-t border-cyan-400/20 px-5 py-4 text-sm leading-6 text-blue-200"><strong className="text-cyan-200">How to read the map:</strong> the United States leads architecture, software and equipment; the Netherlands controls the EUV gateway; Taiwan concentrates advanced fabrication and packaging; South Korea supplies HBM; Japan anchors materials and tools. No country owns the complete chain.</figcaption>
          </figure>
          <div>
            <div className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-cyan-300">2031 scenario frame</div>
            <div className="grid gap-4 md:grid-cols-3">
              {
                [
                  ['55%', 'Continued U.S. leadership', 'The present lead in models, capital, compute and allied chip access persists.'],
                  ['35%', 'Bipolar AI order', 'U.S.- and China-centered technology ecosystems harden into competing stacks.'],
                  ['10%', 'Multipolar shift', 'Open models, regional clouds and new chip capacity distribute power more broadly.'],
                ].map(([pct, label, explanation]) => <div key={pct} className="rounded-xl border border-blue-400/20 bg-blue-950/45 p-5"><div className="text-3xl font-black text-cyan-300">{pct}</div><div className="mt-2 font-bold text-white">{label}</div><p className="mt-2 text-sm leading-6 text-blue-200">{explanation}</p></div>)
              }
            </div>
            <p className="mt-4 text-xs leading-relaxed text-blue-300">These percentages express the report's structured judgment as of August 2026. They are not measured probabilities, investment advice or guarantees.</p>
          </div>
        </div>

        <Card className="h-fit border-amber-300/35 bg-[#0F1A2B]/95 text-white shadow-2xl shadow-black/30"><CardContent className="p-7 md:p-9">
          <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-full border-amber-300/45 bg-amber-300/10 text-amber-200"><LockKeyhole /></div>
          <h2 className="text-3xl font-black">Full publication edition</h2>
          <p className="mt-4 leading-relaxed text-blue-100">The detailed 26-page report is controlled for qualified policy, research, national-security, semiconductor, infrastructure, investment and media readers.</p>
          <ul className="mt-5 space-y-3 text-sm text-blue-100">
            {
              ['Geopolitical and economic assessment','AI and semiconductor dominance maps','Dependency architecture and scenario analysis','Decision framework for credible exit options'].map((item) => <li key={item} className="flex gap-2"><ShieldCheck className="mt-0.5 shrink-0 text-cyan-300" size={16} />{item}</li>)
            }
          </ul>
          {authenticated ? (
            <div className="mt-7 space-y-3"><Button asChild className="w-full bg-emerald-400 font-bold text-[#07101f] hover:bg-emerald-300"><a href="/api/ai-sovereignty-report" target="_blank" rel="noopener noreferrer"><UnlockKeyhole className="mr-2" size={18} />Open Full Report</a><p className="text-center text-xs text-emerald-200">Access verified for this browser.</p></Button></div>
          ) : (
            <form onSubmit={unlock} className="mt-7 space-y-3">
              <label htmlFor="report-password" className="block text-sm font-bold text-white">Access password</label>
              <input id="report-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required className="w-full rounded-lg border border-blue-400/35 bg-[#07101f] px-4 py-3 text-white outline-none ring-cyan-300 focus:ring-2" placeholder="Enter the password supplied by SALAR" />
              {error && <p role="alert" className="text-sm text-red-300">{error}</p>}
              <button type="submit" disabled={busy} className="w-full bg-amber-300 font-bold text-[#07101f] hover:bg-amber-200">{busy ? 'Verifying…' : 'Unlock Full Report'}</button>
            </form>
          )}
          <div className="mt-6 border-t border-blue-400/20 pt-5 text-sm leading-relaxed text-blue-200">Need access? Email <a className="font-bold text-cyan-300 underline" href="mailto:vikrant@salartechnologies.com?subject=Request%20access%20to%20SALAR-WP-2026-006">vikrant@salartechnologies.com</a> with your organization, role and intended use.</div>
        </CardContent></Card>
      </section>

      <section className="border-t border-cyan-400/20 bg-[#0b1728] py-12 sm:py-14">
        <div className="container mx-auto max-w-7xl px-5 sm:px-6">
          <div className="rounded-2xl border border-cyan-400/25 bg-[#0F1A2B] p-6 sm:p-8">
            <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              <div className="max-w-3xl">
                <div className="flex items-center gap-3 text-cyan-300"><Newspaper size={22} /><span className="font-black uppercase tracking-[0.18em]">News & Updates</span></div>
                <h2 className="mt-3 text-2xl font-black sm:text-3xl">Publication announcement and future revisions</h2>
                <p className="mt-3 leading-7 text-blue-100">Read the official release note for SALAR-WP-2026-006 and follow subsequent research, map and publication updates from SALAR Technologies.</p>
              </div>
              <a href="/news-updates" className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-cyan-400 px-5 py-3 font-bold text-[#07101f] transition-colors hover:bg-cyan-300">Open News & Updates <ArrowRight size={18} /></a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
